import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { Httpservice } from '../../services/httpservice';
import { ManageSession } from '../../services/manage-session';
import { SessionService } from '../../services/session.service';

function passwordMatchValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirm_password')?.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  };
}

@Component({
  selector: 'app-update-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './update-password.html',
  styleUrl: './update-password.scss',
})
export class UpdatePassword {
  protected readonly logoPath = 'assets/images/authentication/nea-logo.png';
  private readonly unableToUpdatePasswordMessage = 'Unable to update password';

  protected readonly form: FormGroup;
  protected resetToken = '';

  protected error = '';
  protected isSubmitting = false;
  protected isLinkChecking = false;
  protected isLinkValid = false;
  protected linkMessage = '';
  protected showPassword = false;
  protected showConfirmPassword = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly httpSvc: Httpservice,
    private readonly sessionSvc: ManageSession,
    private readonly appSessionSvc: SessionService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirm_password: ['', [Validators.required]],
      },
      { validators: passwordMatchValidator() },
    );
  }

  ngOnInit(): void {
    this.resetToken = String(this.route.snapshot.queryParamMap.get('reset_token') ?? '').trim();

    if (this.resetToken) {
      this.validateResetToken();
      return;
    }

    if (this.appSessionSvc.getVerificationState() === true) {
      this.router.navigate(['/dashboard']);
      return;
    }

    if (!this.sessionSvc.getAuthToken()) {
      this.router.navigate(['/signin']);
    }

    this.isLinkValid = true;
  }

  protected updatePassword(): void {
    this.error = '';

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      password: String(this.form.getRawValue().password ?? '').trim(),
      confirm_password: String(this.form.getRawValue().confirm_password ?? '').trim(),
      reset_token: this.resetToken || null,
    };

    this.isSubmitting = true;

    this.httpSvc
      .requestCall(AuthEndPoints.UPDATE_PASSWORD, ApiMethod.PATCH, payload)
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (this.isPasswordUpdateSuccessful(response)) {
            const hasAuthToken = Boolean(this.sessionSvc.getAuthToken());
            if (this.resetToken && !hasAuthToken) {
              this.appSessionSvc.clearVerificationState();
              this.router.navigate(['/signin']);
              return;
            }

            this.appSessionSvc.setVerificationState(true);
            this.router.navigate(['/dashboard']);
            return;
          }

          this.error = this.resolvePasswordUpdateError(response);
        },
        error: (error) => {
          this.isSubmitting = false;
          this.error = this.resolvePasswordUpdateError(error?.error ?? error);
        },
      });
  }

  protected togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  protected toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  private validateResetToken(): void {
    if (!this.resetToken) {
      this.isLinkValid = false;
      this.linkMessage = 'Reset link is invalid';
      return;
    }

    this.isLinkChecking = true;
    this.linkMessage = '';
    this.error = '';

    this.httpSvc
      .requestCall(AuthEndPoints.VERIFY_FORGOT_PASSWORD_LINK, ApiMethod.POST, {
        reset_token: this.resetToken,
      })
      .subscribe({
        next: (response) => {
          this.isLinkChecking = false;
          this.cdr.markForCheck();
          if (this.isResetLinkValid(response)) {
            this.isLinkValid = true;
            this.linkMessage = '';
            return;
          }

          this.isLinkValid = false;
          this.linkMessage = this.resolveResetLinkMessage(response);
        },
        error: (error) => {
          this.isLinkChecking = false;
          this.isLinkValid = false;
          this.linkMessage = this.resolveResetLinkMessage(error?.error ?? error);
          this.cdr.markForCheck();
        },
      });
  }

  private isPasswordUpdateSuccessful(response: any): boolean {
    return response?.status === 'TRUE' && response?.value?.is_verified === true;
  }

  private isResetLinkValid(response: any): boolean {
    return response?.status === 'TRUE' && Boolean(response?.value?.link_id);
  }

  private resolveResetLinkMessage(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg.trim() : '';

    if (message) {
      return message;
    }

    if (response?.status === 'FALSE' && response?.value) {
      return 'Reset link is invalid or expired';
    }

    return 'Reset link is invalid or expired';
  }

  private resolvePasswordUpdateError(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg : '';
    const passwordMismatch = this.form.errors?.['passwordMismatch'];

    if (message) {
      return message;
    }

    if (passwordMismatch) {
      return 'Password and confirm password must match';
    }

    return this.unableToUpdatePasswordMessage;
  }
}
